import { LEGACY_EN_REDIRECTS } from "@/config/legacy-urls";
import { defaultLocale, type Locale } from "./config";

// Dış adres ↔ iç yol eşlemesi. Saf fonksiyonlar: proxy (src/proxy.ts), sayfalar ve birim testleri kullanır.
//
// İç yol = Türkçe yol (app/[lang] altındaki klasör adları). Türkçe dış adres iç yolla aynıdır (/hakkimizda);
// İngilizce dış adreste ilk parça çevrilir (/en/about-us → iç /en/hakkimizda). Tur adresinin alt parçası (tam ad + kod)
// dile göre farklıdır: /turlar/elegant-…-6-gece-ebp-sv6n ↔ /en/tours/elegant-…-6-nights-ebp-sv6n; eşleme tur kodu ile
// (features/tours/slugs.ts, services/urls.ts). Burada yalnızca ilk parça çevrilir (Türkçe parçalı İngilizce adres tur
// sayfasında kanonik adrese 308 alır).

/** Türkçe ilk yol parçası → İngilizce karşılığı. Burada olmayan Türkçe adresin İngilizcesi yoktur. */
export const EN_SEGMENTS = {
  turlar: "tours",
  destinasyonlar: "destinations",
  urunlerimiz: "our-products",
  flydmc: "flydmc",
  hakkimizda: "about-us",
  iletisim: "contact",
  acentelerimiz: "our-agencies",
  "acente-talep-formu": "agency-request-form",
  "kvkk-aydinlatma": "personal-data-protection",
  "gizlilik-politikasi": "privacy-policy",
  "cerez-politikasi": "cookie-policy",
  "avrupa-turlari": "europe-tours",
  "asya-turlari": "asia-tours",
  "afrika-turlari": "africa-tours",
  "amerika-turlari": "america-tours",
  "kruvaziyer-turlari": "cruise-tours",
  "fly-cruise": "fly-cruise",
  "fly-exclusive": "fly-exclusive",
  "fly-elegant": "fly-elegant",
  "fly-classic": "fly-classic",
  // İlk dört FlyDMC ofisinin eski İngilizce adresleri (korunur). Panelden eklenen ofisin adresi iki dilde aynıdır: OFFICE_SLUG
  "fly-tayland": "fly-thailand",
  "fly-fas": "fly-morocco",
  "fly-endonezya": "fly-indonesia",
  "fly-avrupa": "fly-europe",
} as const satisfies Record<string, string>;

type TrSegment = keyof typeof EN_SEGMENTS;

/** Kök düzeyde kodla ayrılmış teknik adresler (dil önekleri, API, görseller, metadata dosyaları, iç işaretler) */
const TECHNICAL_ROOT = [
  "en", "tr", "api", "medya", "brand", "flags", "_next", "sitemap", "sitemap.xml", "robots", "robots.txt", "icon", "apple-icon",
  "opengraph-image", "sayfa", "admin", "panel", "giris", "__sayfa", "__yok",
] as const;

/**
 * Kök düzeyde (/x ve /en/x) sitenin kendi sayfalarına ayrılmış adresler: sabit sayfalar, bölgeler, ürün aileleri, FlyDMC
 * ofisleri (Türkçe ve İngilizce adlarıyla) ve teknik yollar. Panelde yönetilen sayfa bunları alamaz
 * (panel shared/site-content.ts → RESERVED_SLUGS, RESERVED_EN_SLUGS); destinasyon (kategori) sayfası bunlarla aynı adı
 * taşıyorsa köke çıkamaz, /destinasyonlar/<adres> adresinde kalır (categoryPath). Birim testi: tests/unit/category-urls.test.ts
 */
export const RESERVED_ROOT_SLUGS: ReadonlySet<string> = new Set<string>([...Object.keys(EN_SEGMENTS), ...Object.values(EN_SEGMENTS), ...TECHNICAL_ROOT]);

/**
 * FlyDMC ofis adresi biçimi: "fly-" + küçük harf, rakam, tire (/fly-misir). Ofisler Site Yönetimi'nden eklenir; adres iki
 * dilde aynıdır (/fly-misir ↔ /en/fly-misir), yalnızca ilk dört ofisin eski İngilizce adresleri farklıdır (EN_SEGMENTS).
 * Bu biçimdeki bütün kök adresler ofislere ayrılmıştır: panel yönetilen sayfaya vermez, destinasyon köke çıkmaz, tur
 * verisi yokken 503 almaz. Ürün aileleri (/fly-cruise …) de bu biçimdedir ama EN_SEGMENTS'te oldukları için ofis sayılmaz.
 */
export const OFFICE_SLUG = /^fly-[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Kökte sitenin kendi sayfasına ya da FlyDMC ofislerine ayrılmış adres mi (yönetilen sayfalar hariç) */
export const isReservedRootSlug = (slug: string) => RESERVED_ROOT_SLUGS.has(slug) || OFFICE_SLUG.test(slug);

/** Kök adres ayrılmış mı: sabit liste + (sunucuda) yayındaki yönetilen sayfaların Türkçe ve İngilizce adresleri */
export type RootReservation = (slug: string) => boolean;
export const staticReservation: RootReservation = isReservedRootSlug;

/**
 * Destinasyon (kategori) sayfasının bu dildeki adresi (ürün sahibi kararı, 2026-10-08): kökte /italya-turlari, İngilizcede
 * /en/italya-turlari (adres parçası iki dilde aynı, Türkçe addan; değişmez). Adres ayrılmışsa (bölge sayfası
 * /avrupa-turlari, yönetilen sayfa …) çakışma olmasın diye eski yerinde kalır: /destinasyonlar/<adres>,
 * /en/destinations/<adres>. Karar iki dilde aynıdır (ayrılmış küme iki dilin adreslerini birlikte içerir).
 */
export function categoryPath(locale: Locale, slug: string, reserved: RootReservation = staticReservation): string {
  const root = !reserved(slug);
  if (locale === defaultLocale) return root ? `/${slug}` : `/destinasyonlar/${slug}`;
  return root ? `/en/${slug}` : `/en/destinations/${slug}`;
}

/** Destinasyon sayfasının iki dildeki adresi (hreflang, dil seçici) */
export function categoryAlternates(slug: string, reserved: RootReservation = staticReservation): { tr: string; en: string } {
  return { tr: categoryPath("tr", slug, reserved), en: categoryPath("en", slug, reserved) };
}

/**
 * Proxy → sayfa: istek adresi kanonik biçimde değildi (sondaki "/" ya da büyük harf; proxy sadeleştirip yeniden yazdı).
 * Kendi kanonik adresini kuran sayfalar (tur detayı, eski destinasyon adresi, tur listesi) bu başlıkla, adres zaten kanonik
 * görünse de yönlendirir: eski/bozuk adres tek adımda kanonik adrese gider. Dışarıdan gelen değer proxy'de silinir.
 */
export const CANONICALIZE_HEADER = "x-fe-kanonik";

/**
 * Proxy → tur listesi düzeni: isteğin sorgu metni (düzenler searchParams almaz). Tek filtreli liste (yalnızca bölge, ürün
 * ailesi ya da destinasyon) yükleme iskeleti akışı başlamadan, düzende gerçek 308 ile o filtrenin sayfasına yönlenir.
 */
export const LIST_QUERY_HEADER = "x-fe-liste-sorgu";

/** Yönetilen İngilizce sayfaların iç yol işareti (app/[lang]/[...rest]) */
export const MANAGED_PAGE_SEGMENT = "__sayfa";
const MANAGED_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Alt yolu olan bölümler (diğerlerinde /en/about-us/x gibi adres 404'tür) */
const NESTED: ReadonlySet<string> = new Set<TrSegment>(["turlar", "destinasyonlar"]);

const TR_BY_EN: ReadonlyMap<string, TrSegment> = new Map(
  (Object.entries(EN_SEGMENTS) as [TrSegment, string][]).map(([tr, en]) => [en, tr]),
);

/** Eski sitenin yönlendirilen İngilizce adresleri (/en/fly-cruise-2 …; next.config): ofis adresi olamaz */
const LEGACY_SOURCES: ReadonlySet<string> = new Set(LEGACY_EN_REDIRECTS.map((r) => r.source.split("/").pop()!));

/** Ofis adresi olabilecek ilk parça (sabit sayfa, ürün ailesi, eski İngilizce ofis adresi ya da eski yönlendirme değil): iki dilde aynı */
const isOfficeSegment = (segment: string) => OFFICE_SLUG.test(segment) && !(segment in EN_SEGMENTS) && !TR_BY_EN.has(segment) && !LEGACY_SOURCES.has(segment);

function splitPath(path: string): { pathname: string; suffix: string } {
  const i = path.search(/[?#]/);
  return i === -1 ? { pathname: path, suffix: "" } : { pathname: path.slice(0, i), suffix: path.slice(i) };
}

/** Bu Türkçe (iç) yolun İngilizce karşılığı var mı (yönetilen sayfalar yalnızca Türkçedir) */
export function hasEnglish(trPath: string): boolean {
  const { pathname } = splitPath(trPath);
  const [first, ...rest] = pathname.split("/").filter(Boolean);
  if (!first) return true;
  if (!(first in EN_SEGMENTS)) return isOfficeSegment(first) && rest.length === 0;
  return rest.length === 0 || (NESTED.has(first) && rest.length === 1);
}

/**
 * Türkçe (iç) yoldan verilen dildeki dış adres: ("en", "/turlar/abc?kalkis=1") → "/en/tours/abc?kalkis=1".
 * İngilizcesi olmayan yol için İngilizce ana sayfa döner.
 */
export function localizedPath(locale: Locale, trPath: string): string {
  if (locale === defaultLocale) return trPath || "/";
  const { pathname, suffix } = splitPath(trPath);
  if (!hasEnglish(pathname)) return "/en";
  const [first, ...rest] = pathname.split("/").filter(Boolean);
  if (!first) return `/en${suffix}`;
  return ["/en", (EN_SEGMENTS as Record<string, string>)[first] ?? first, ...rest].join("/") + suffix;
}

export type Resolution =
  | { kind: "rewrite"; locale: Locale; internal: string }
  | { kind: "redirect"; location: string };

/**
 * Proxy için: tarayıcıdaki adres (yalnızca yol) → iç yol (/tr/…, /en/…) veya kalıcı yönlendirme.
 * - /tr ve /tr/… dışarıdan açılmaz → Türkçe kök adrese yönlenir (kopya içerik olmaz).
 * - /en/<Türkçe parça> (ör. /en/hakkimizda) → İngilizce adrese yönlenir.
 * - Bilinmeyen adresler iç yola aynen geçer; sayfa 404 verir.
 * - /en/fly-<ad>: FlyDMC ofisi, iç yol aynı adres (OFFICE_SLUG).
 * - /en/<tek parça> başka bir şeye uymuyorsa Site Yönetimi'nin İngilizce sayfası olabilir: iç /en/__sayfa/<adres>
 *   (yayında değilse sayfa 404 verir). Çok parçalı bilinmeyen adres: /en/__yok/… (404).
 */
export function resolvePath(pathname: string): Resolution {
  const parts = pathname.split("/").filter(Boolean);
  const [first, second, ...rest] = parts;
  if (first === "tr") return { kind: "redirect", location: "/" + parts.slice(1).join("/") };
  if (first !== "en") return { kind: "rewrite", locale: "tr", internal: `/tr${pathname === "/" ? "" : pathname}` };
  if (!second) return { kind: "rewrite", locale: "en", internal: "/en" };
  const tr = TR_BY_EN.get(second);
  if (tr) return { kind: "rewrite", locale: "en", internal: ["/en", tr, ...rest].join("/") };
  if (second in EN_SEGMENTS) return { kind: "redirect", location: ["/en", EN_SEGMENTS[second as TrSegment], ...rest].join("/") };
  // FlyDMC ofisi (/en/fly-misir): iç yol aynı adres; ofis yoksa sayfa 404 verir
  if (rest.length === 0 && isOfficeSegment(second)) return { kind: "rewrite", locale: "en", internal: `/en/${second}` };
  if (rest.length === 0 && MANAGED_SLUG.test(second)) return { kind: "rewrite", locale: "en", internal: `/en/${MANAGED_PAGE_SEGMENT}/${second}` };
  return { kind: "rewrite", locale: "en", internal: `/en/__yok/${[second, ...rest].join("/")}` };
}

/** Dış adresten (tarayıcı yolu) Türkçe iç yol: "/en/tours/abc" → "/turlar/abc"; çözülemezse null */
export function toTrPath(pathname: string): string | null {
  const r = resolvePath(pathname);
  if (r.kind === "redirect") return toTrPath(r.location);
  if (r.internal.includes("/__yok/") || r.internal.includes(`/${MANAGED_PAGE_SEGMENT}/`)) return null;
  return r.internal.replace(/^\/(tr|en)/, "") || "/";
}

/** Dil değiştirici: iç yol parçalarından (useSelectedLayoutSegments) hedef dildeki adres */
export function switchPath(trSegments: string[], target: Locale): string {
  // Yönetilen İngilizce sayfanın Türkçe adresi yoldan bilinmez: sayfa dil seçiciye kendisi verir (AlternateLinks)
  if (trSegments.includes("__yok") || trSegments.includes(MANAGED_PAGE_SEGMENT)) return localizedPath(target, "/");
  const clean = trSegments.filter((s) => s && !s.startsWith("(") && !s.startsWith("__"));
  const trPath = "/" + clean.join("/");
  if (target === "en" && !hasEnglish(trPath)) return "/en";
  return localizedPath(target, trPath);
}

/** hreflang alternatifleri (metadata ve site haritası için, köke göre göreli) */
export function languageAlternates(trPath: string): Record<string, string> {
  if (!hasEnglish(trPath)) return {};
  return { tr: localizedPath("tr", trPath), en: localizedPath("en", trPath), "x-default": localizedPath("tr", trPath) };
}

// --- İngilizce sayfalardaki site içi bağlantılar ---

/** İngilizce sayfada site içi bağlantıyı çevirmek için gerekenler (Site Yönetimi'nin yayındaki sayfaları ve site kökü) */
export type LinkContext = {
  /** Yayındaki yönetilen sayfalar: Türkçe adres → İngilizce adres (İngilizce sürümü yayında değilse null) */
  managedPages: ReadonlyMap<string, string | null>;
  /** Sitenin kendi kökü (SITE_URL): bu köke giden mutlak bağlantılar da site içi sayılır */
  siteOrigin?: string | null;
  /** Kökteki destinasyon (kategori) sayfalarının adresleri (/italya-turlari ↔ /en/italya-turlari); verilmezse yok sayılır */
  rootCategories?: ReadonlySet<string>;
};

export type LocalizedLink = { href: string; hreflang?: "tr" };

/** Sayfa olmayan site içi yollar (görsel, API, dosya): bağlantı olduğu gibi kalır */
const NON_PAGE = /^\/(?:api|medya|brand|flags|_next)(?:\/|$)|\.[a-z0-9]{2,5}$/i;

function sameSite(url: URL, origin: string | null | undefined): boolean {
  if (!origin) return false;
  try {
    const site = new URL(origin);
    const host = (h: string) => h.toLowerCase().replace(/^www\./, "");
    return /^https?:$/.test(url.protocol) && host(url.host) === host(site.host);
  } catch {
    return false;
  }
}

/**
 * Site Yönetimi metnindeki bağlantının İngilizce sayfadaki karşılığı (saklanan metin değişmez; gösterirken çevrilir):
 * "/iletisim" → "/en/contact", "/turlar?bolge=avrupa" → "/en/tours?bolge=avrupa", "/hakkimizda#x" → "/en/about-us#x",
 * İngilizce sürümü yayındaki yönetilen sayfa "/kampanya" → "/en/<İngilizce adres>". Sitenin kendi köküne giden mutlak
 * bağlantı da çevrilir (kök korunur). İngilizcesi olmayan Türkçe sayfaya giden bağlantı Türkçe kalır ve hreflang="tr"
 * alır. Dış bağlantılar, mailto/tel, sayfa içi (#) bağlantılar, /en/… ve dosya/görsel yolları değişmez.
 */
export function englishHref(href: string, ctx: LinkContext): LocalizedLink {
  const raw = href.trim();
  let origin = "";
  let path = raw;
  if (/^https?:\/\//i.test(raw)) {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return { href };
    }
    if (!sameSite(url, ctx.siteOrigin)) return { href };
    origin = url.origin;
    path = url.pathname + url.search + url.hash;
  }
  if (!path.startsWith("/") || path.startsWith("//")) return { href };
  const { pathname: rawPathname, suffix } = splitPath(path);
  let pathname = rawPathname.length > 1 ? rawPathname.replace(/\/+$/, "") || "/" : rawPathname;
  if (NON_PAGE.test(pathname)) return { href };
  const first = pathname.split("/").filter(Boolean)[0];
  if (first === "en") return { href };
  // /tr/… dışarıdan Türkçe köke yönlenir (resolvePath): aynı Türkçe sayfa
  if (first === "tr") pathname = "/" + pathname.split("/").filter(Boolean).slice(1).join("/");

  const out = (p: string): LocalizedLink => ({ href: origin + p });
  const parts = pathname.split("/").filter(Boolean);
  // Kökteki destinasyon sayfası (/italya-turlari) ve eski adresi (/destinasyonlar/italya-turlari) → /en/italya-turlari
  const category = parts.length === 1 ? parts[0] : parts.length === 2 && parts[0] === "destinasyonlar" ? parts[1] : null;
  if (category && ctx.rootCategories?.has(category)) return out(`/en/${category}${suffix}`);
  if (hasEnglish(pathname)) return out(localizedPath("en", pathname + suffix));
  if (parts.length === 1 && ctx.managedPages.has(parts[0])) {
    const en = ctx.managedPages.get(parts[0]);
    if (en) return out(`/en/${en}${suffix}`);
  }
  // İngilizce karşılığı yok (İngilizce sürümü yayında olmayan yönetilen sayfa vb.): Türkçe sayfa
  return { href, hreflang: "tr" };
}
